import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { authApi } from "@/api/authApi";
import { ApiError } from "@/api/http";
import type { AuthState, Perfil } from "@/types/auth";

interface AuthContextValue extends AuthState {
  setPerfil: (perfil: Perfil | null) => void;
  recarregar: () => Promise<void>;
  sair: () => Promise<void>;
  saindo: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [saindo, setSaindo] = useState(false);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await authApi.me();
      setPerfil(res.autenticado && res.perfil ? res.perfil : null);
    } catch {
      setPerfil(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const sair = useCallback(async () => {
    if (saindo) return;
    setSaindo(true);
    try {
      await authApi.sair();
    } catch (err) {
      // 401 => sessão já encerrada, segue normalmente para o login
      if (!(err instanceof ApiError && err.status === 401)) {
        setSaindo(false);
        throw new Error("Não foi possível encerrar a sessão. Tente novamente.");
      }
    }
    setPerfil(null);
    setSaindo(false);
  }, [saindo]);

  const value = useMemo<AuthContextValue>(
    () => ({
      autenticado: perfil != null,
      perfil,
      carregando,
      setPerfil,
      recarregar,
      sair,
      saindo,
    }),
    [perfil, carregando, recarregar, sair, saindo],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return ctx;
}
